import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const WhoWeServe = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  const audiences = [
    {
      key: 'peBackedCompanies',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
    },
    {
      key: 'boltOnAcquisitions',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
    },
    {
      key: 'highGrowthSMBs',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>{t.meta.whoWeServe.title}</title>
        <meta name="description" content={t.meta.whoWeServe.description} />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0a1628] to-[#0f1f38] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t.whoWeServe.title}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t.whoWeServe.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-4">
              {t.whoWeServe.howWeHelp}
            </h2>
          </div>

          <div className="space-y-24">
            {audiences.map((audience, index) => {
              const audienceData = t.whoWeServe[audience.key];
              return (
                <div key={audience.key} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                  {/* Image */}
                  <div className="lg:w-1/2">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={audience.image}
                        alt={audienceData.title}
                        className="w-full h-96 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/40 to-transparent" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:w-1/2">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#0a1628] mb-3">
                      {audienceData.title}
                    </h3>
                    <p className="text-xl text-[#2ecc8a] font-medium mb-6">
                      {audienceData.subtitle}
                    </p>
                    <p className="text-lg text-gray-600 leading-relaxed mb-8">
                      {audienceData.description}
                    </p>

                    {/* Pain Points */}
                    {audienceData.painPoints && audienceData.painPoints.length > 0 && (
                      <div className="mb-8">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                          {language === 'fr' ? 'Points de douleur communs' : 'Common Pain Points'}
                        </h4>
                        <div className="grid grid-cols-1 gap-3">
                          {audienceData.painPoints.map((point, idx) => (
                            <div key={idx} className="flex items-start space-x-3">
                              <span className="text-red-500 mt-1">•</span>
                              <span className="text-gray-600">{point}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Benefits */}
                    <div>
                      <h4 className="text-lg font-bold text-[#0a1628] mb-4">
                        {language === 'fr' ? 'Comment nous aidons' : 'How We Help'}
                      </h4>
                      <ul className="space-y-3">
                        {audienceData.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <Check className="text-[#2ecc8a] flex-shrink-0 mt-1" size={20} />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.whoWeServe.ctaTitle}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.whoWeServe.ctaSubtitle}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#2ecc8a] text-white font-semibold rounded-lg hover:bg-[#26b377] transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {t.whoWeServe.letsTalk}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhoWeServe;
